import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { hubFetch } from '@/lib/hubUrl';
import { log } from '@/lib/logger';

export type Role = 'admin' | 'pilot' | 'observer' | 'analyst';

interface AuthState {
  /** Bearer token returned by /auth/login. Null when logged out. */
  token: string | null;
  username: string | null;
  role: Role | null;
  displayName: string | null;
  loginError: string | null;

  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  /** Verify the persisted token is still valid; clears state if not. */
  rehydrate: () => Promise<void>;
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      username: null,
      role: null,
      displayName: null,
      loginError: null,

      async login(username, password) {
        try {
          const resp = await hubFetch('/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username, password }),
          });
          if (!resp.ok) {
            set({ loginError: resp.status === 401 ? 'Wrong username or password' : `HTTP ${resp.status}` });
            return false;
          }
          const body = (await resp.json()) as {
            token: string;
            username: string;
            role: Role;
            display_name: string | null;
          };
          set({
            token: body.token,
            username: body.username,
            role: body.role,
            displayName: body.display_name,
            loginError: null,
          });
          log.auth(`login · ${body.username} (${body.role})`);
          return true;
        } catch (e: any) {
          set({ loginError: e?.message ?? 'network error' });
          return false;
        }
      },

      async logout() {
        const tok = get().token;
        if (tok) {
          try {
            await hubFetch('/auth/logout', {
              method: 'POST',
              headers: { Authorization: `Bearer ${tok}` },
            });
          } catch {
            /* ignore */
          }
        }
        log.auth('logout');
        set({ token: null, username: null, role: null, displayName: null });
      },

      async rehydrate() {
        const tok = get().token;
        if (!tok) return;
        try {
          const resp = await hubFetch('/auth/whoami', {
            headers: { Authorization: `Bearer ${tok}` },
          });
          if (!resp.ok) {
            set({ token: null, username: null, role: null, displayName: null });
          }
        } catch {
          /* offline: keep token, let user retry */
        }
      },
    }),
    { name: 'fabricos-auth-v1' },
  ),
);

/** Returns the current bearer token (or null). */
export function authHeader(): Record<string, string> {
  const tok = useAuth.getState().token;
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}
